import { Form, Input, Modal } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { AiOutlineClose, AiOutlineFilePdf } from "react-icons/ai";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { cloudFrontUrl, serverUrl } from "../../../../nestserver";
import { selectUser } from "../../../../redux/slices/auth.slice";

const HomeWorkBroadcast = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [subjectId, setSubjectId] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const isTeacher = false;
  const messageContainerRef = React.useRef(null);
  const [classSubjectData, setClassSubjectData] = useState([]);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  const handleImageClick = (src) => {
    setModalImageSrc(src);
    setIsModalVisible(true);
  };

  const handleVideoClick = (videoUrl) => {
    setSelectedVideo(videoUrl);
    setVideoModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalImageSrc("");
  };

  const handleSubjectChange = async (subjectId) => {
    setSubjectId(subjectId);

    if (subjectId) {
      try {
        let url =
          serverUrl + "/conversations/subject/" + subjectId + "/read-message";
        if (subjectId === "all-subjects") {
          const studentClassId =
            user?.student?.class?._id || user?.student?.class_id;
          if (studentClassId) {
            url =
              serverUrl +
              "/conversations/class/" +
              studentClassId +
              "/read-message";
          } else {
            return;
          }
        }
        await axios({
          method: "patch",
          url: url,
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        queryClient.invalidateQueries(["students", user?._id]);
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    }
  };

  const fetchClassSubject = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/students/" + user?._id + "/subject-teacher/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    const subjectsWithUnreadCounts = await Promise.all(
      res?.data?.result?.subjects?.map(async (subject) => {
        try {
          const conversationRes = await axios({
            method: "get",
            url: serverUrl + "/conversations/subject/" + subject?._id,
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem(
                "jwt-token",
              )}`,
            },
          });

          return {
            ...subject,
            unread_messages_count:
              conversationRes?.data?.result?.unread_messages_count || 0,
          };
        } catch (error) {
          console.error(
            `Error fetching unread count for subject ${subject._id}:`,
            error,
          );
          return {
            ...subject,
            unread_messages_count: 0,
          };
        }
      }) || [],
    );

    // Fetch class broadcast unread count
    let classBroadcastUnreadCount = 0;
    try {
      const studentClassId =
        user?.student?.class?._id || user?.student?.class_id;
      if (studentClassId) {
        const classConversationRes = await axios({
          method: "get",
          url: serverUrl + "/conversations/class/" + studentClassId,
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        classBroadcastUnreadCount =
          classConversationRes?.data?.result?.unread_messages_count || 0;
      }
    } catch (error) {
      console.error("Error fetching class broadcast unread count", error);
    }

    setClassSubjectData({
      ...res.data,
      classBroadcastUnreadCount,
      result: {
        ...res.data.result,
        subjects: subjectsWithUnreadCounts,
      },
    });

    return res.data;
  };

  useQuery(["students", user?._id], fetchClassSubject, {
    enabled: !!user?._id,
  });

  const fetchClassConversation = async (id) => {
    let url = serverUrl + "/conversations/subject/" + id;
    if (id === "all-subjects") {
      const studentClassId =
        user?.student?.class?._id || user?.student?.class_id;
      if (studentClassId) {
        url = serverUrl + "/conversations/class/" + studentClassId;
      } else {
        return { result: { messages: [] } };
      }
    }
    const res = await axios({
      method: "get",
      url: url,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };

  const classConversationDetails = useQuery(
    ["conversations", subjectId],
    () => fetchClassConversation(subjectId),
    {
      enabled: !!subjectId,
      onSuccess: () => {
        if (subjectId) {
          let url =
            serverUrl + "/conversations/subject/" + subjectId + "/read-message";
          if (subjectId === "all-subjects") {
            const studentClassId =
              user?.student?.class?._id || user?.student?.class_id;
            if (studentClassId) {
              url =
                serverUrl +
                "/conversations/class/" +
                studentClassId +
                "/read-message";
            } else {
              return;
            }
          }
          axios({
            method: "patch",
            url: url,
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem(
                "jwt-token",
              )}`,
            },
          })
            .then(() => {
              queryClient.invalidateQueries(["students", user?._id]);
            })
            .catch((error) => {
              console.error("Error marking messages as read:", error);
            });
        }
      },
    },
  );

  useEffect(() => {
    scrollToBottom();
  }, [classConversationDetails]);

  // Helper function to determine media folder and type
  const getMediaInfo = (item) => {
    let folder = "images"; // Default
    let mediaType = item?.type;

    if (item?.type) {
      if (item.type === "IMAGE") folder = "images";
      else if (item.type === "PDF") folder = "pdf";
      else if (item.type === "VIDEO") folder = "video";
      else if (item.type === "AUDIO" || item.type === "VOICE") folder = "audio";
    } else {
      // Fallback to extension-based detection if type is not explicitly set
      const name = item?.name?.toLowerCase() || "";
      if (name.endsWith(".pdf")) {
        folder = "pdf";
        mediaType = "PDF";
      } else if (name.endsWith(".mp4")) {
        folder = "video";
        mediaType = "VIDEO";
      } else if (name.endsWith(".mp3")) {
        folder = "audio";
        mediaType = "AUDIO";
      } else if (name.endsWith(".wav") || name.endsWith(".webm")) {
        folder = "audio";
        mediaType = "VOICE";
      } else if (
        name.endsWith(".jpeg") ||
        name.endsWith(".jpg") ||
        name.endsWith(".png")
      ) {
        folder = "images";
        mediaType = "IMAGE";
      }
    }
    return { folder, mediaType };
  };

  return (
    <div>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full screen-height "
        >
          <div className="flex gap-4 min-height">
            <div style={{ width: "30%" }} className="message-left-part">
              <Form
                form={form}
                name="dynamic_rule"
                layout="vertical"
                autoComplete="off"
                initialValues={{
                  student: `${user?.student?.first_name} ${user?.student?.last_name}`,
                }}
              >
                <Form.Item name="student" label="Student Name" className="w-30">
                  <Input readOnly />
                </Form.Item>
                <Form.Item name="subject" label="Subject" className="w-30">
                  <div
                    style={{
                      maxHeight: "calc(100vh - 320px)",
                      overflowY: "auto",
                    }}
                  >
                    {classSubjectData?.result && (
                      <div className="pt-2 text-base cursor-pointer">
                        <div
                          onClick={() => handleSubjectChange("all-subjects")}
                          type="primary"
                          size="large"
                          className={`cursor-pointer ${
                            subjectId === "all-subjects"
                              ? "active-message"
                              : "message-card"
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>All Subjects</div>
                            {classSubjectData?.classBroadcastUnreadCount >
                              0 && (
                              <div className="rounded-full border flex justify-center items-center unread-message">
                                <p>
                                  {classSubjectData?.classBroadcastUnreadCount}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {classSubjectData &&
                      classSubjectData?.result?.subjects?.map((data) => (
                        <div
                          className="pt-2 text-base cursor-pointer"
                          key={data?._id}
                        >
                          <div
                            onClick={() => handleSubjectChange(data?._id)}
                            type="primary"
                            size="large"
                            className={`cursor-pointer ${
                              subjectId === data?._id
                                ? "active-message"
                                : "message-card"
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div>{data?.name}</div>
                              {data?.unread_messages_count > 0 && (
                                <div className="rounded-full border flex justify-center items-center unread-message">
                                  <p>{data?.unread_messages_count}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </Form.Item>
              </Form>
            </div>
            <div style={{ width: "70%" }} className="instructor-broadcast">
              {subjectId ? (
                <>
                  <div className="flex justify-between items-center pb-4">
                    <h1 className="pb-2 text-base font-semibold">
                      Class BroadCast
                    </h1>
                    <AiOutlineClose
                      size={20}
                      onClick={() => {
                        setSubjectId(null);
                        form.resetFields(["subject", "class"]);
                      }}
                      className="cursor-pointer"
                    />
                  </div>
                  <div
                    className="message-chat-content-class pt-8 "
                    style={{ width: "70%", marginLeft: "auto" }}
                    ref={messageContainerRef}
                  >
                    {classConversationDetails?.data?.result?.messages ? (
                      classConversationDetails?.data?.result?.messages?.map(
                        (message) => {
                          return (
                            <div key={message?._id}>
                              <div
                                className="flex items-center gap-4 pb-4 justify-end"
                                style={{ marginRight: "12px" }}
                              >
                                <div className="flex flex-col gap-4">
                                  <span>
                                    {message?.sender?.teacher &&
                                      `${message?.sender?.teacher?.first_name} ${message?.sender?.teacher?.last_name} `}
                                  </span>
                                  <span>
                                    {moment(message?.createdAt).format(
                                      "DD-MM-YYYY hh:mm A",
                                    )}
                                  </span>
                                </div>

                                <div
                                  className={`${
                                    isTeacher
                                      ? "message-box"
                                      : "message-box-other"
                                  }`}
                                >
                                  {message?.media &&
                                  Array.isArray(message.media)
                                    ? message.media.map((mediaItem, index) => {
                                        const { folder, mediaType } =
                                          getMediaInfo(mediaItem);
                                        const mediaUrl = `${cloudFrontUrl}/${folder}/${mediaItem?.id}`;

                                        return (
                                          <div
                                            key={mediaItem.id || index}
                                            className="media-container"
                                          >
                                            {mediaType === "VIDEO" ? (
                                              <div
                                                className="relative cursor-pointer group"
                                                onClick={() =>
                                                  handleVideoClick(mediaUrl)
                                                }
                                              >
                                                <video
                                                  className="media-video"
                                                  style={{
                                                    maxWidth: "100%",
                                                    borderRadius: "8px",
                                                    pointerEvents: "none",
                                                  }}
                                                >
                                                  <source
                                                    src={mediaUrl}
                                                    type="video/mp4"
                                                  />
                                                  Your browser does not support
                                                  the video tag.
                                                </video>
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors rounded-lg">
                                                  <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                                                    <div className="w-0 h-0 border-l-[12px] border-l-black border-y-[8px] border-y-transparent ml-1"></div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : mediaType === "AUDIO" ||
                                              mediaType === "VOICE" ? (
                                              <div className="flex flex-col items-center">
                                                <audio
                                                  controls
                                                  className="media-audio w-full min-w-[250px]"
                                                  style={{
                                                    width: "100%",
                                                    minWidth: "250px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f0f2f5",
                                                    padding: "5px",
                                                  }}
                                                >
                                                  <source src={mediaUrl} />
                                                  Your browser does not support
                                                  the audio tag.
                                                </audio>
                                                <div
                                                  style={{
                                                    fontSize: "12px",
                                                    marginTop: "4px",
                                                    wordBreak: "break-all",
                                                  }}
                                                >
                                                  {mediaItem?.name}
                                                </div>
                                              </div>
                                            ) : mediaType === "IMAGE" ? (
                                              <img
                                                src={mediaUrl}
                                                alt={mediaItem?.name}
                                                onClick={() =>
                                                  handleImageClick(mediaUrl)
                                                }
                                                className="media-image cursor-pointer"
                                                onError={(e) => {
                                                  e.target.onerror = null;
                                                  e.target.src =
                                                    "fallback-image-url";
                                                }}
                                              />
                                            ) : mediaType === "PDF" ? (
                                              <a
                                                href={mediaUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="pdf-link"
                                              >
                                                <AiOutlineFilePdf size={35} />
                                                {mediaItem?.name}
                                              </a>
                                            ) : null}
                                          </div>
                                        );
                                      })
                                    : message?.media && (
                                        <div className="media-container">
                                          {(() => {
                                            const { folder, mediaType } =
                                              getMediaInfo(message.media);
                                            const mediaUrl = `${cloudFrontUrl}/${folder}/${message.media?.id}`;

                                            if (mediaType === "VIDEO") {
                                              return (
                                                <div
                                                  className="relative cursor-pointer group"
                                                  onClick={() =>
                                                    handleVideoClick(mediaUrl)
                                                  }
                                                >
                                                  <video
                                                    className="media-video"
                                                    style={{
                                                      maxWidth: "100%",
                                                      borderRadius: "8px",
                                                      pointerEvents: "none",
                                                    }}
                                                  >
                                                    <source
                                                      src={mediaUrl}
                                                      type="video/mp4"
                                                    />
                                                    Your browser does not
                                                    support the video tag.
                                                  </video>
                                                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors rounded-lg">
                                                    <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                                                      <div className="w-0 h-0 border-l-[12px] border-l-black border-y-[8px] border-y-transparent ml-1"></div>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            } else if (
                                              mediaType === "AUDIO" ||
                                              mediaType === "VOICE"
                                            ) {
                                              return (
                                                <div className="flex flex-col items-center">
                                                  <audio
                                                    controls
                                                    className="media-audio w-full min-w-[250px]"
                                                    style={{
                                                      width: "100%",
                                                      minWidth: "250px",
                                                      borderRadius: "8px",
                                                      backgroundColor:
                                                        "#f0f2f5",
                                                      padding: "5px",
                                                    }}
                                                  >
                                                    <source src={mediaUrl} />
                                                    Your browser does not
                                                    support the audio tag.
                                                  </audio>
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      marginTop: "4px",
                                                      wordBreak: "break-all",
                                                    }}
                                                  >
                                                    {message.media?.name}
                                                  </div>
                                                </div>
                                              );
                                            } else if (mediaType === "IMAGE") {
                                              return (
                                                <img
                                                  src={mediaUrl}
                                                  alt={message?.media?.name}
                                                  onClick={() =>
                                                    handleImageClick(mediaUrl)
                                                  }
                                                  className="media-image cursor-pointer"
                                                  onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src =
                                                      "fallback-image-url";
                                                  }}
                                                />
                                              );
                                            } else if (mediaType === "PDF") {
                                              return (
                                                <a
                                                  href={mediaUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="pdf-link"
                                                >
                                                  <AiOutlineFilePdf size={35} />
                                                  {message.media?.name}
                                                </a>
                                              );
                                            } else {
                                              return null;
                                            }
                                          })()}
                                        </div>
                                      )}
                                  {message?.message}

                                  <Modal
                                    visible={isModalVisible}
                                    footer={null}
                                    onCancel={handleCancel}
                                    centered
                                  >
                                    <img
                                      src={modalImageSrc}
                                      alt="Preview"
                                      style={{
                                        width: "100%",
                                        marginTop: "20px",
                                      }}
                                    />
                                  </Modal>
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )
                    ) : (
                      <div className="flex items-center h-full">
                        No message found
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center h-full w-full">
                  No message found
                </div>
              )}
            </div>
          </div>
          <div className="flex"></div>
        </div>
      </div>

      <Modal
        visible={videoModalVisible}
        footer={null}
        onCancel={() => {
          setVideoModalVisible(false);
          setSelectedVideo(null);
        }}
        width={800}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, backgroundColor: "black" }}
      >
        {selectedVideo && (
          <video
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "80vh", display: "block" }}
          >
            <source src={selectedVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </Modal>
    </div>
  );
};

export default HomeWorkBroadcast;
